import {
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Radio from '@mui/material/Radio';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomButton } from '../../../common/button/customButton';

export interface FilterOption {
  label: string;
  value: string;
}

export interface SimpleDialogProps {
  title: string;
  open: boolean;
  selectedOptions: string[];
  onClose: () => void;
  onApply: (selectedOptions: string[]) => void;
  options: FilterOption[];
  radio?: boolean;
}

export const FilterDialog = (props: SimpleDialogProps) => {
  const { onClose, onApply, selectedOptions, open, options, radio, title } =
    props;
  const { t } = useTranslation();
  const [selectedOptionsFromList, setSelectedOptionsFromList] =
    useState(selectedOptions);

  useEffect(() => {
    setSelectedOptionsFromList(selectedOptions);
  }, [selectedOptions]);

  const handleToggle = (value: string) => () => {
    if (radio) {
      if (selectedOptionsFromList.some((seloption) => seloption !== value))
        setSelectedOptionsFromList([value]);
      return;
    }

    const currentIndex = selectedOptionsFromList.indexOf(value);
    const newChecked = [...selectedOptionsFromList];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedOptionsFromList(newChecked);
  };

  const handleOnClose = () => {
    onClose();
    if (radio && selectedOptions.some((seloption) => seloption === '')) {
      if (selectedOptions.length === 1) {
        setSelectedOptionsFromList(['']);
      } else {
        setSelectedOptionsFromList(options.map((option) => option.value));
      }
    } else {
      setSelectedOptionsFromList(selectedOptions);
    }
  };

  const handleOnApply = () => {
    onApply(selectedOptionsFromList);
  };

  return (
    <Dialog
      onClose={handleOnClose}
      open={open}
      fullWidth
      maxWidth="sm"
      sx={{ minHeight: '80vh' }}
    >
      <DialogTitle sx={{ alignSelf: 'center' }}>{title}</DialogTitle>
      <FormControlLabel
        sx={{ alignSelf: 'center', width: '90%' }}
        control={
          <Checkbox
            edge="start"
            checked={
              radio
                ? selectedOptionsFromList.some(
                    (selectedOption) => selectedOption === '',
                  )
                : selectedOptionsFromList.length === options.length
            }
            tabIndex={-1}
            disableRipple
            onChange={(val) => {
              if (radio) {
                if (val.target.checked === true) {
                  setSelectedOptionsFromList(['']);
                }
              } else if (val.target.checked === true) {
                setSelectedOptionsFromList(
                  options.map((option) => option.value),
                );
              } else {
                setSelectedOptionsFromList([]);
              }
            }}
          />
        }
        label={t('AllSelected')}
      />
      <DialogContent dividers>
        <List sx={{ pt: 0, maxHeight: '40em' }}>
          {options.map((option) => (
            <ListItem key={option.value} disablePadding>
              <ListItemButton
                role={undefined}
                onClick={handleToggle(option.value)}
                dense
              >
                <ListItemIcon>
                  {radio && (
                    <Radio
                      edge="start"
                      checked={
                        selectedOptionsFromList.some(
                          (selectedOptionFromList) =>
                            selectedOptionFromList === '',
                        ) && selectedOptionsFromList.length === 1
                          ? true
                          : selectedOptionsFromList.some(
                              (selectedOption) =>
                                selectedOption === option.value,
                            )
                      }
                      tabIndex={-1}
                      disableRipple
                    />
                  )}
                  {!radio && (
                    <Checkbox
                      edge="start"
                      checked={selectedOptionsFromList.some(
                        (selectedOption) => selectedOption === option.value,
                      )}
                      tabIndex={-1}
                      disableRipple
                    />
                  )}
                </ListItemIcon>
                <ListItemText id={option.value} primary={option.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <CustomButton text={t('Cancel')} handleOnClick={handleOnClose} />
        <CustomButton text={t('Apply')} handleOnClick={handleOnApply} />
      </DialogActions>
    </Dialog>
  );
};

FilterDialog.defaultProps = {
  radio: false,
};
